import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ship, Wrench, Users, UserPlus, ArrowRight, X, Sparkles, Check, ListChecks } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/context/AuthContext";

/**
 * Shown on the dashboard for admin users of a NEW workspace with no data yet.
 * Guides through 4 quick actions. Dismissible; remembers dismissal in localStorage.
 */
export default function OnboardingWizard() {
  const { lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const isES = lang === "es";

  const [counts, setCounts] = useState(null);
  const [visible, setVisible] = useState(false);

  const dismissKey = `sns_onboarding_dismissed_${user?.tenant_id || ""}`;

  const [forced, setForced] = useState(false);
  useEffect(() => {
    const open = () => { setForced(true); setVisible(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
    window.addEventListener("sns:open-guide", open);
    return () => window.removeEventListener("sns:open-guide", open);
  }, []);

  useEffect(() => {
    if (!user || user?.role !== "admin") return;
    const dismissed = localStorage.getItem(dismissKey) === "true";
    if (dismissed && !forced) return;
    let cancelled = false;
    (async () => {
      try {
        const [srv, cli, emb, users, mant] = await Promise.all([
          api.get("/servicios"),
          api.get("/clientes"),
          api.get("/embarcaciones"),
          api.get("/users"),
          api.get("/mantenimientos"),
        ]);
        if (cancelled) return;
        const props = emb.data || [];
        const c = {
          services: (srv.data || []).length,
          clients: (cli.data || []).length,
          properties: props.length,
          checklists: props.filter((p) => (p.checkouts || []).length > 0).length,
          employees: (users.data || []).filter(u => u?.role === "empleado").length,
          jobs: (mant.data || []).length,
        };
        setCounts(c);
        // Se muestra hasta crear el primer mantenimiento
        if (c.jobs === 0 || forced) setVisible(true);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, forced]);

  const dismiss = () => {
    localStorage.setItem(dismissKey, "true");
    setVisible(false);
    setForced(false);
  };

  if (!visible || !counts) return null;

  const steps = [
    {
      id: "services",
      Icon: Sparkles,
      color: "#C2410C",
      bg: "#FFE6D7",
      done: counts.services > 0,
      title: isES ? "Crea tus servicios" : "Create your services",
      desc: isES
        ? "Limpieza interior, cristales, lavado de cubierta… con su precio. Luego los añades a cada mantenimiento."
        : "Interior cleaning, windows, deck wash… with their price. You'll add them to each job.",
      cta: isES ? "Crear servicios" : "Create services",
      onClick: () => nav("/gestion/servicios"),
    },
    {
      id: "client",
      Icon: UserPlus,
      color: "#2D62B7",
      bg: "#E8F1FF",
      done: counts.clients > 0,
      title: isES ? "Crea el propietario" : "Create the owner",
      desc: isES
        ? "El dueño de la villa o el barco (empresa o particular). Sus propiedades quedarán vinculadas a él."
        : "The villa or boat owner (company or individual). Their properties will be linked to them.",
      cta: isES ? "Nuevo propietario" : "New owner",
      onClick: () => nav("/gestion/clientes"),
    },
    {
      id: "property",
      Icon: Ship,
      color: "#0369A1",
      bg: "#E0F2FE",
      done: counts.properties > 0,
      title: isES ? "Crea la propiedad" : "Create the property",
      desc: isES
        ? "Villa o embarcación del propietario, con ubicación, matrícula y notas."
        : "The owner's villa or boat, with location, registration and notes.",
      cta: isES ? "Añadir propiedad" : "Add property",
      onClick: () => nav("/gestion/embarcaciones"),
    },
    {
      id: "checklist",
      Icon: ListChecks,
      color: "#166534",
      bg: "#DCFCE7",
      done: counts.checklists > 0,
      title: isES ? "Crea su checklist" : "Create its checklist",
      desc: isES
        ? "Las zonas a revisar en cada propiedad (cocina, baños, cubierta…). Es obligatoria para finalizar un mantenimiento."
        : "Areas to check in each property (kitchen, bathrooms, deck…). Required to finish a job.",
      cta: isES ? "Añadir checklist" : "Add checklist",
      onClick: () => nav("/gestion/embarcaciones"),
    },
    {
      id: "employee",
      Icon: Users,
      color: "#7C3AED",
      bg: "#F3E8FF",
      done: counts.employees > 0,
      title: isES ? "Invita a tu equipo" : "Invite your team",
      desc: isES
        ? "Tus empleados solo verán los trabajos que les asignes y marcarán los servicios hechos."
        : "Employees only see the jobs you assign and tick the services they did.",
      cta: isES ? "Añadir empleado" : "Add employee",
      onClick: () => nav("/gestion/usuarios"),
    },
    {
      id: "job",
      Icon: Wrench,
      color: "#B45309",
      bg: "#FEF3C7",
      done: counts.jobs > 0,
      title: isES ? "Programa el primer mantenimiento" : "Schedule the first job",
      desc: isES
        ? "Elige la propiedad, los servicios (o crea nuevos ahí mismo), el equipo y la fecha."
        : "Pick the property, services (or create new ones right there), team and date.",
      cta: isES ? "Nuevo mantenimiento" : "New job",
      onClick: () => nav("/nuevo-mantenimiento"),
    },
  ];

  const doneCount = steps.filter(s => s.done).length;
  const totalCount = steps.length;
  const percent = Math.round((doneCount / totalCount) * 100);

  return (
    <div className="sns-card p-5 md:p-6 mb-6 bg-gradient-to-br from-white to-slate-50 relative overflow-hidden" data-testid="onboarding-wizard">
      {/* Decorative sparkle */}
      <div className="absolute -top-4 -right-4 w-32 h-32 rounded-full bg-[var(--coral)]/5 blur-2xl pointer-events-none" />

      <button onClick={dismiss}
              className="absolute top-3 right-3 w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 flex items-center justify-center"
              aria-label="Cerrar"
              data-testid="onboarding-dismiss">
        <X size={16} />
      </button>

      <div className="flex items-center gap-3 mb-5 pr-8">
        <div className="w-11 h-11 rounded-xl bg-[var(--coral)]/10 text-[var(--coral)] flex items-center justify-center">
          <Sparkles size={20} strokeWidth={2.4} />
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold text-[var(--coral)] tracking-widest uppercase mb-0.5">
            {isES ? "Empieza en 30 segundos" : "Get started in 30 seconds"}
          </div>
          <h2 className="sns-title text-xl">
            {isES ? "Configura tu empresa en 6 pasos" : "Set up your company in 6 steps"}
          </h2>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-5">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="font-bold text-slate-600">{doneCount} / {totalCount}</span>
          <span className="text-slate-400">{percent}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[var(--coral)] to-[#FF9976] transition-all duration-500"
               style={{ width: `${percent}%` }} />
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {steps.map((s, idx) => (
          <div key={s.id}
               className={`p-4 rounded-xl border-2 transition-all ${
                 s.done
                   ? "border-emerald-200 bg-emerald-50/50"
                   : "border-slate-200 hover:border-[var(--coral)]/40 hover:bg-white bg-white"
               }`}
               data-testid={`onboarding-step-${s.id}`}>
            <div className="flex items-start gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                   style={{ background: s.done ? "#DCFCE7" : s.bg, color: s.done ? "#166534" : s.color }}>
                {s.done ? <Check size={18} strokeWidth={3} /> : <s.Icon size={16} strokeWidth={2.4} />}
              </div>
              <div className="text-[10px] font-black text-slate-400 tracking-widest">
                {isES ? "PASO" : "STEP"} {idx + 1}
              </div>
            </div>
            <h3 className={`font-bold text-sm mb-1 ${s.done ? "text-emerald-700" : "text-slate-800"}`}>
              {s.title}
            </h3>
            <p className="text-xs text-slate-500 mb-3 leading-relaxed">{s.desc}</p>
            {s.done ? (
              <div className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1">
                <Check size={12} /> {isES ? "Completado" : "Done"}
              </div>
            ) : (
              <button onClick={s.onClick}
                      className="text-xs font-bold text-[var(--coral)] hover:underline inline-flex items-center gap-1"
                      data-testid={`onboarding-cta-${s.id}`}>
                {s.cta} <ArrowRight size={12} />
              </button>
            )}
          </div>
        ))}
      </div>

      {doneCount === totalCount && (
        <div className="mt-4 p-3 bg-emerald-50 rounded-xl text-sm text-emerald-700 font-semibold text-center" data-testid="onboarding-complete">
          {isES ? "¡Empresa configurada! Ya puedes empezar a usar la app a fondo." : "Company ready! You can now use the app fully."}
        </div>
      )}
    </div>
  );
}


export const OpenGuideButton = () => {
  const { user } = useAuth();
  if (user?.role !== "admin") return null;
  return (
    <button
      onClick={() => window.dispatchEvent(new Event("sns:open-guide"))}
      className="w-full mb-6 flex items-center gap-3 p-4 rounded-2xl bg-[var(--sky)] text-[var(--sky-text)] font-bold text-left active:scale-[0.99] transition-transform"
      data-testid="btn-open-guide"
    >
      <ListChecks size={20} />
      <span className="flex-1">Guía de configuración: de los servicios a tu primer mantenimiento</span>
      <ArrowRight size={18} />
    </button>
  );
};
